import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['banner', 'button', 'infoContent']
  static values = { pwaInfoUrl: String }

  DISMISSAL_KEY = 'pwa-install-dismissed'
  DISMISSAL_DURATION = 7 * 24 * 60 * 60 * 1000 // 1 week
  ANDROID_CHECK_INTERVAL = 500
  ANDROID_CHECK_TIMEOUT = 10000
  SHOW_DELAY_IOS = 2000
  SHOW_DELAY_ANDROID = 3000

  async connect() {

    this.isAlreadyInstalled = null
    this.canBeInstalled = null

    try {
      this.deferredPrompt = null
      this.isIOS = this.detectIOS()
      this.isAndroid = this.detectAndroid()
      this.isStandalone = this.detectStandalone()

      if (this.isStandalone) {
        // Clear caches when running in standalone mode to prevent authentication issues
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'CLEAR_CACHE'
          });
        }
        return this.hideBanner()
      }

      if (await this.hasInstalledRelatedApp()) {
        return this.hideBanner()
      }

      this.setupEventListeners()

      if (this.shouldShowBanner()) {
        this.scheduleBannerDisplay()
      }
    } catch (error) {
      console.warn('[PWA Install] Error in connect, continuing without PWA features:', error);
      // Continue without PWA functionality rather than breaking the app
    }
  }

  disconnect() {
    window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt)
    window.removeEventListener('appinstalled', this.handleAppInstalled)
  }

  // ========== Platform Detection ==========

  detectIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream ||
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  }

  detectAndroid() {
    return /Android/.test(navigator.userAgent)
  }

  detectStandalone() {
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.navigator.standalone ||
           document.referrer.includes('android-app://') ||
           navigator.userAgent.includes('VesselindexApp')
  }

  async hasInstalledRelatedApp() {
    if (!('getInstalledRelatedApps' in navigator)) return false

    try {
      const relatedApps = await navigator.getInstalledRelatedApps()
      return Array.isArray(relatedApps) && relatedApps.length > 0
    } catch (error) {
      console.warn('[PWA Install] getInstalledRelatedApps failed:', error)
      return false
    }
  }

  // ========== Event Handlers ==========

  setupEventListeners() {
    window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt.bind(this))
    window.addEventListener('appinstalled', this.handleAppInstalled.bind(this))
  }

  handleBeforeInstallPrompt(event) {
    event.preventDefault()
    this.canBeInstalled = true
    this.deferredPrompt = event
    this.showPromptForPlatform()
  }

  handleAppInstalled() {
    this.isAlreadyInstalled = true
    this.hideBanner()
  }

  async install() {
    if (!this.deferredPrompt) {
      return this.loadInfo()
    }

    this.deferredPrompt.prompt()
    const { outcome } = await this.deferredPrompt.userChoice

    this.deferredPrompt = null
    if (outcome === 'accepted') {
      this.hideBanner()
    }
  }

  async loadInfo() {
    if (!this.hasInfoContentTarget || this.infoLoaded) return

    try {
      const response = await fetch(this.pwaInfoUrlValue, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })

      this.infoContentTarget.innerHTML = await response.text()
      this.infoLoaded = true
    } catch (error) {
      this.infoContentTarget.textContent = 'Unable to load app information right now.'
    }
  }

  dismiss() {
    window.dispatchEvent(new CustomEvent('show-confirmation', {
      detail: {
        title: 'Dismiss app install?',
        message: `Are you sure you don't want to install the VESSELINDEX® app?<br>The banner won't show anymore.`,
        confirmLabel: `No, I don't want`,
        cancelLabel: 'Install app',
        onConfirm: () => {
          localStorage.setItem(this.DISMISSAL_KEY, Date.now().toString())
          this.hideBanner()
        },
        onCancel: () => {
          this.install()
        }
      }
    }))
  }

  // ========== Prompt Display Logic ==========

  wasDismissed() {
    const dismissedTime = parseInt(localStorage.getItem(this.DISMISSAL_KEY) || '0', 10)
    return dismissedTime > Date.now() - this.DISMISSAL_DURATION
  }

  shouldShowBanner() {

    if (this.isAlreadyInstalled || this.wasDismissed()) return false
    if (!this.isIOS && !this.canBeInstalled) return false

    return true
  }

  scheduleBannerDisplay() {
    const delay = this.isIOS ? this.SHOW_DELAY_IOS : this.SHOW_DELAY_ANDROID

    setTimeout(() => this.showPromptForPlatform(), delay)
  }

  showPromptForPlatform() {
    if (!this.hasBannerTarget || !this.shouldShowBanner()) return

    this.showBanner()
  }

  // ========== UI Manipulation ==========

  showBanner() {
    this.showElement(this.bannerTarget)
  }

  hideBanner() {
    this.hideElement(this.bannerTarget)
  }

  showElement(element) {
    if (element) {
      element.classList.remove('d-none')
      element.classList.add('show')
    }
  }

  hideElement(element) {
    if (element) {
      element.classList.add('d-none')
      element.classList.remove('show')
    }
  }

  // ========== Button Management ==========

  setupAndroidButtonWatcher() {
    // Periodically check if deferredPrompt arrived
    const checkInterval = setInterval(() => {
      if (this.deferredPrompt) {
        this.updateInstallButtonVisibility()
        clearInterval(checkInterval)
      }
    }, this.ANDROID_CHECK_INTERVAL)

    setTimeout(() => clearInterval(checkInterval), this.ANDROID_CHECK_TIMEOUT)

  }
}
